import React, { useState, useEffect, createContext, useContext } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './amplifyconfiguration';
import Header from './components/Header/Header';
import LiquiditySweepChart from './components/Chart/LiquiditySweep/LiquiditySweepChart';
import FVGChart from './components/Chart/FVGChart/FVGChart';
import LiquidityZoneChart from './components/Chart/LiquidityZone/LiquidityZoneChart';
import OrderBookChart from './components/Chart/OrderBookChart';
import SwingHighLowChart from './components/Chart/SwingHLChart';
import LSTMPredictChart from './components/Chart/LSTMPredictChart';
import NewsAndSentiment from './components/News/NewsAndSentiment';
import TradingStrategyConfig from './components/TradingStrategy/TradingStrategyConfig';
import Login from './components/Auth/Login';
import { AuthProvider, useAuth } from './components/Auth/AuthContext';
import g50Currencies from './constants/g50Currencies';
import timeIntervals from './constants/timeIntervals';
import timeZones from './constants/timeZones';
import TOOLTIP_INFO from './constants/tooltipInfo';

import styles from './App.module.css';
import InstructionPage from './components/LandingPage.tsx';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import CookiePolicy from './components/CookiePolicy';
import Plot from "react-plotly.js";
import LoadingSpinner from "./components/Loader/LoadingSpinner";
import Dropdown from "./components/Dropdown/Dropdown";
import SideDrawer from './components/SideDrawer/SideDrawer';
import TradeSetups from './components/TradeSetups/TradeSetups.tsx';
import {
  HiOutlineHome,
  HiOutlinePresentationChartLine,
  HiOutlineSparkles,
  HiOutlineArrowTrendingUp,
  HiOutlineCog6Tooth,
} from 'react-icons/hi2';

// Create a context for the shared state
const AppContext = createContext();

// Custom hook to access AppContext
export const useAppContext = () => useContext(AppContext);

// Update TOOLTIP_INFO to include Trading Strategy
const updatedTooltipInfo = {
  ...TOOLTIP_INFO,
  'Trading Strategy': 'Configure and manage automated trading strategies with support for multiple platforms and execution series.',
  'CFTC Snapshot': 'View consolidated CFTC futures position breakdowns.',
  'Trade Setups': 'Scan intraday charts for rule-based setup signals.'
};

const themeOptions = [
  { value: 'system', label: 'System' },
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' }
];

const fontSizeOptions = [
  { value: '14px', label: 'Small (14px)' },
  { value: '16px', label: 'Medium (16px)' },
  { value: '18px', label: 'Large (18px)' }
];

const fontFamilyOptions = [
  { value: 'system', label: 'System' },
  { value: 'inter', label: 'Inter' },
  { value: 'roboto', label: 'Roboto' },
  { value: 'ibm', label: 'IBM Plex Sans' }
];

const DEFAULT_TYPE_SCALE_PRESETS = {
  title: { small: '1.35rem', medium: '1.5rem', large: '1.65rem' },
  section: { small: '1.1rem', medium: '1.25rem', large: '1.4rem' },
  body: { small: '0.85rem', medium: '0.9rem', large: '1rem' },
  label: { small: '0.7rem', medium: '0.75rem', large: '0.85rem' },
  caption: { small: '0.65rem', medium: '0.75rem', large: '0.85rem' },
  tiny: { small: '0.6rem', medium: '0.65rem', large: '0.75rem' }
};

const DEFAULT_TYPE_SCALE_CHOICE = {
  title: 'medium',
  section: 'medium',
  body: 'medium',
  label: 'medium',
  caption: 'medium',
  tiny: 'medium'
};

const inferTypeScaleChoice = (value, preset) => {
  if (!preset) return 'medium';
  if (value === preset.small) return 'small';
  if (value === preset.medium) return 'medium';
  if (value === preset.large) return 'large';
  return 'medium';
};

const getInitialTypeScalePresets = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_TYPE_SCALE_PRESETS;
  }
  try {
    const stored = window.localStorage.getItem('ui.typeScalePresets');
    return stored
      ? { ...DEFAULT_TYPE_SCALE_PRESETS, ...JSON.parse(stored) }
      : DEFAULT_TYPE_SCALE_PRESETS;
  } catch (err) {
    return DEFAULT_TYPE_SCALE_PRESETS;
  }
};

const getInitialTypeScaleChoice = (presets) => {
  if (typeof window === 'undefined') {
    return DEFAULT_TYPE_SCALE_CHOICE;
  }
  try {
    const storedChoice = window.localStorage.getItem('ui.typeScaleChoice');
    if (storedChoice) {
      return { ...DEFAULT_TYPE_SCALE_CHOICE, ...JSON.parse(storedChoice) };
    }
    const storedScale = window.localStorage.getItem('ui.typeScale');
    if (storedScale) {
      const parsed = JSON.parse(storedScale);
      return {
        title: inferTypeScaleChoice(parsed.title, presets.title),
        section: inferTypeScaleChoice(parsed.section, presets.section),
        body: inferTypeScaleChoice(parsed.body, presets.body),
        label: inferTypeScaleChoice(parsed.label, presets.label),
        caption: inferTypeScaleChoice(parsed.caption, presets.caption),
        tiny: inferTypeScaleChoice(parsed.tiny, presets.tiny)
      };
    }
  } catch (err) {
    return DEFAULT_TYPE_SCALE_CHOICE;
  }
  return DEFAULT_TYPE_SCALE_CHOICE;
};

const buildTypeScaleOptions = (presets) => ({
  title: [
    { value: 'small', label: `Small (${presets.title.small})` },
    { value: 'medium', label: `Medium (${presets.title.medium})` },
    { value: 'large', label: `Large (${presets.title.large})` }
  ],
  section: [
    { value: 'small', label: `Small (${presets.section.small})` },
    { value: 'medium', label: `Medium (${presets.section.medium})` },
    { value: 'large', label: `Large (${presets.section.large})` }
  ],
  body: [
    { value: 'small', label: `Small (${presets.body.small})` },
    { value: 'medium', label: `Medium (${presets.body.medium})` },
    { value: 'large', label: `Large (${presets.body.large})` }
  ],
  label: [
    { value: 'small', label: `Small (${presets.label.small})` },
    { value: 'medium', label: `Medium (${presets.label.medium})` },
    { value: 'large', label: `Large (${presets.label.large})` }
  ],
  caption: [
    { value: 'small', label: `Small (${presets.caption.small})` },
    { value: 'medium', label: `Medium (${presets.caption.medium})` },
    { value: 'large', label: `Large (${presets.caption.large})` }
  ],
  tiny: [
    { value: 'small', label: `Small (${presets.tiny.small})` },
    { value: 'medium', label: `Medium (${presets.tiny.medium})` },
    { value: 'large', label: `Large (${presets.tiny.large})` }
  ]
});

const getInitials = (name, email) => {
  const source = name || email || '';
  const parts = source.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'U';
  if (parts.length === 1) return (parts[0][0] || 'U').toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

function UserMenu({ user, theme, setTheme, onNavigateSettings, onSignOut, open, setOpen }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof open === 'boolean' && typeof setOpen === 'function';
  const menuOpen = isControlled ? open : internalOpen;
  const setMenuOpen = isControlled ? setOpen : setInternalOpen;
  const menuRef = React.useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen, setMenuOpen]);

  const initials = getInitials(user?.name, user?.email);

  return (
    <div className={styles.userMenu} ref={menuRef}>
      <button
        type="button"
        className={styles.userMenuButton}
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        {user?.picture ? (
          <img className={styles.userAvatar} src={user.picture} alt={user?.name || 'User'} />
        ) : (
          <span className={styles.userAvatarFallback} aria-hidden="true">{initials}</span>
        )}
      </button>
      {menuOpen && (
        <div className={styles.userMenuDropdown} role="menu">
          <div className={styles.userMenuMeta}>
            <div className={styles.userMenuName}>{user?.name || 'User'}</div>
            {user?.email && <div className={styles.userMenuEmail}>{user.email}</div>}
          </div>
          <button type="button" className={styles.userMenuItem} onClick={onNavigateSettings}>
            Settings
          </button>
          <div className={styles.userMenuSection}>
            <span className={styles.userMenuLabel}>Theme</span>
            <select
              className={styles.userMenuSelect}
              value={theme}
              onChange={(event) => setTheme(event.target.value)}
            >
              {themeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className={styles.userMenuItem} onClick={onSignOut}>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

function SettingsPage() {
  const {
    theme,
    setTheme,
    fontFamily,
    setFontFamily,
    fontSize,
    setFontSize,
    typeScaleChoice,
    setTypeScaleChoice,
    typeScalePresets,
    setTypeScalePresets
  } = useAppContext();
  const typeScaleOptions = React.useMemo(
    () => buildTypeScaleOptions(typeScalePresets),
    [typeScalePresets]
  );
  const [cftcFile, setCftcFile] = useState(null);
  const [cftcUrl, setCftcUrl] = useState('https://www.cftc.gov/dea/newcot/FinComWk.txt');
  const [cftcUploadStatus, setCftcUploadStatus] = useState('No file selected.');
  const [cftcUploadBusy, setCftcUploadBusy] = useState(false);
  const [cftcUploadMeta, setCftcUploadMeta] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      return JSON.parse(window.localStorage.getItem('cftcUploadMeta') || 'null');
    } catch (err) {
      return null;
    }
  });
  const categories = [
    { id: 'account', label: 'Account' },
    { id: 'appearance', label: 'Appearance' },
    { id: 'data', label: 'Data Management' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'security', label: 'Security' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'trading', label: 'Trading / Preferences' }
  ];

  const [activeCategory, setActiveCategory] = useState(() => {
    if (typeof window === 'undefined') return 'appearance';
    return window.location.hash.replace('#', '') || 'appearance';
  });

  useEffect(() => {
    const handleHash = () => {
      const next = window.location.hash.replace('#', '');
      if (next) {
        setActiveCategory(next);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  const handleCategoryClick = (id) => {
    setActiveCategory(id);
    if (typeof window !== 'undefined') {
      window.location.hash = id;
    }
  };

  const handleCftcFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setCftcFile(file);
    if (!file) {
      setCftcUploadStatus('No file selected.');
      return;
    }
    const allowed = ['.zip', '.txt', '.csv'];
    const isAllowed = allowed.some((ext) => file.name.toLowerCase().endsWith(ext));
    setCftcUploadStatus(
      isAllowed
        ? `Selected: ${file.name}`
        : 'Unsupported file type. Please upload .zip, .txt, or .csv.'
    );
  };

  const handleCftcUpload = async () => {
    if (!cftcFile || cftcUploadBusy) {
      return;
    }
    setCftcUploadBusy(true);
    setCftcUploadStatus('Uploading and processing…');
    try {
      const base =
        process.env.REACT_APP_CFTC_IMPORT_API_BASE ||
        process.env.REACT_APP_FX_INTRADAY_API_BASE ||
        'https://vidplay-api.deng-software.com/fx';
      const formData = new FormData();
      formData.append('file', cftcFile);
      const response = await fetch(`${base}/cftc/import`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }
      const payload = await response.json().catch(() => ({}));
      const meta = {
        name: cftcFile.name,
        timestamp: new Date().toISOString(),
        summary: payload?.summary || payload?.message || 'Upload complete.',
      };
      setCftcUploadMeta(meta);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('cftcUploadMeta', JSON.stringify(meta));
      }
      setCftcUploadStatus('Upload complete. Data is now available in the system.');
    } catch (err) {
      setCftcUploadStatus(err?.message || 'Upload failed. Please try again.');
    } finally {
      setCftcUploadBusy(false);
    }
  };

  const handleCftcUrlFetch = async () => {
    if (!cftcUrl || cftcUploadBusy) {
      return;
    }
    setCftcUploadBusy(true);
    setCftcUploadStatus('Fetching and processing…');
    try {
      const base =
        process.env.REACT_APP_CFTC_IMPORT_API_BASE ||
        process.env.REACT_APP_FX_INTRADAY_API_BASE ||
        'https://vidplay-api.deng-software.com/fx';
      const response = await fetch(`${base}/cftc/import-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: cftcUrl }),
      });
      if (!response.ok) {
        throw new Error(`Fetch failed (${response.status})`);
      }
      const payload = await response.json().catch(() => ({}));
      const meta = {
        name: cftcUrl,
        timestamp: new Date().toISOString(),
        summary: payload?.summary || payload?.message || 'Import complete.',
      };
      setCftcUploadMeta(meta);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('cftcUploadMeta', JSON.stringify(meta));
      }
      setCftcUploadStatus('Import complete. Data is now available in the system.');
    } catch (err) {
      setCftcUploadStatus(err?.message || 'Import failed. Please try again.');
    } finally {
      setCftcUploadBusy(false);
    }
  };

  return (
    <div className={styles.settingsPage}>
      <aside className={styles.settingsSidebar}>
        <h2 className={styles.settingsTitle}>Settings</h2>
        <nav className={styles.settingsNav}>
          {categories.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`${styles.settingsNavItem} ${
                activeCategory === item.id ? styles.settingsNavItemActive : ''
              }`}
              onClick={() => handleCategoryClick(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </aside>
      <section className={styles.settingsContent}>
        {activeCategory === 'appearance' && (
          <div className={styles.settingsPanel}>
            <h3 className={styles.settingsPanelTitle}>Appearance</h3>
            <p className={styles.settingsPanelDescription}>
              Choose how the interface looks across the app.
            </p>
            <div className={styles.settingRow}>
              <div>
                <div className={styles.settingLabel}>Theme</div>
                <div className={styles.settingHint}>System follows your OS preference.</div>
              </div>
              <select
                className={styles.settingSelect}
                value={theme}
                onChange={(event) => setTheme(event.target.value)}
              >
                {themeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.settingRow}>
              <div>
                <div className={styles.settingLabel}>Global font family</div>
                <div className={styles.settingHint}>
                  Applies across the entire interface.
                </div>
              </div>
              <select
                className={styles.settingSelect}
                value={fontFamily}
                onChange={(event) => setFontFamily(event.target.value)}
              >
                {fontFamilyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.settingRow}>
              <div>
                <div className={styles.settingLabel}>Global font size</div>
                <div className={styles.settingHint}>
                  Adjusts base sizing for all typography.
                </div>
              </div>
              <select
                className={styles.settingSelect}
                value={fontSize}
                onChange={(event) => setFontSize(event.target.value)}
              >
                {fontSizeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.settingRow}>
              <div>
                <div className={styles.settingLabel}>Title size</div>
                <div className={styles.settingHint}>Section titles and primary headers.</div>
              </div>
              <select
                className={styles.settingSelect}
                value={typeScaleChoice.title}
                onChange={(event) =>
                  setTypeScaleChoice((prev) => ({ ...prev, title: event.target.value }))
                }
              >
                {typeScaleOptions.title.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.settingRow}>
              <div>
                <div className={styles.settingLabel}>Section size</div>
                <div className={styles.settingHint}>Secondary panel and card titles.</div>
              </div>
              <select
                className={styles.settingSelect}
                value={typeScaleChoice.section}
                onChange={(event) =>
                  setTypeScaleChoice((prev) => ({ ...prev, section: event.target.value }))
                }
              >
                {typeScaleOptions.section.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.settingRow}>
              <div>
                <div className={styles.settingLabel}>Body size</div>
                <div className={styles.settingHint}>Primary paragraph and summary text.</div>
              </div>
              <select
                className={styles.settingSelect}
                value={typeScaleChoice.body}
                onChange={(event) =>
                  setTypeScaleChoice((prev) => ({ ...prev, body: event.target.value }))
                }
              >
                {typeScaleOptions.body.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.settingRow}>
              <div>
                <div className={styles.settingLabel}>Label size</div>
                <div className={styles.settingHint}>Short labels and inline headings.</div>
              </div>
              <select
                className={styles.settingSelect}
                value={typeScaleChoice.label}
                onChange={(event) =>
                  setTypeScaleChoice((prev) => ({ ...prev, label: event.target.value }))
                }
              >
                {typeScaleOptions.label.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.settingRow}>
              <div>
                <div className={styles.settingLabel}>Caption size</div>
                <div className={styles.settingHint}>Muted helper text and footnotes.</div>
              </div>
              <select
                className={styles.settingSelect}
                value={typeScaleChoice.caption}
                onChange={(event) =>
                  setTypeScaleChoice((prev) => ({ ...prev, caption: event.target.value }))
                }
              >
                {typeScaleOptions.caption.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.settingRow}>
              <div>
                <div className={styles.settingLabel}>Tiny size</div>
                <div className={styles.settingHint}>Badges, ticks, and micro labels.</div>
              </div>
              <select
                className={styles.settingSelect}
                value={typeScaleChoice.tiny}
                onChange={(event) =>
                  setTypeScaleChoice((prev) => ({ ...prev, tiny: event.target.value }))
                }
              >
                {typeScaleOptions.tiny.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.typeScaleSectionHeader}>Type scale presets</div>
            <div className={styles.typeScalePresetGrid}>
              <div className={styles.typeScalePresetHeader}>
                <span />
                <span>Small</span>
                <span>Medium</span>
                <span>Large</span>
              </div>
              {[
                { key: 'title', label: 'Title' },
                { key: 'section', label: 'Section' },
                { key: 'body', label: 'Body' },
                { key: 'label', label: 'Label' },
                { key: 'caption', label: 'Caption' },
                { key: 'tiny', label: 'Tiny' }
              ].map((row) => (
                <div key={row.key} className={styles.typeScalePresetRow}>
                  <span className={styles.typeScalePresetLabel}>{row.label}</span>
                  {['small', 'medium', 'large'].map((sizeKey) => (
                    <input
                      key={sizeKey}
                      className={styles.typeScalePresetInput}
                      type="text"
                      value={typeScalePresets[row.key][sizeKey]}
                      onChange={(event) =>
                        setTypeScalePresets((prev) => ({
                          ...prev,
                          [row.key]: {
                            ...prev[row.key],
                            [sizeKey]: event.target.value
                          }
                        }))
                      }
                      aria-label={`${row.label} ${sizeKey} size`}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div className={styles.typeScalePresetActions}>
              <button
                type="button"
                className={styles.typeScaleResetButton}
                onClick={() => setTypeScalePresets(DEFAULT_TYPE_SCALE_PRESETS)}
              >
                Reset defaults
              </button>
            </div>
          </div>
        )}
        {activeCategory === 'data' && (
          <div className={styles.settingsPanel}>
            <h3 className={styles.settingsPanelTitle}>CFTC Data Import</h3>
            <p className={styles.settingsPanelDescription}>
              Upload the latest CFTC file for processing and inclusion in the system.
            </p>
            <div className={styles.uploadPanel}>
              <div className={styles.uploadRow}>
                <div>
                  <div className={styles.settingLabel}>Pull from URL</div>
                  <div className={styles.settingHint}>Default: FinComWk.txt (latest)</div>
                </div>
                <input
                  type="text"
                  className={styles.uploadFileInput}
                  value={cftcUrl}
                  onChange={(event) => setCftcUrl(event.target.value)}
                />
              </div>
              <div className={styles.uploadRow}>
                <div className={styles.uploadStatus}>
                  {cftcUploadBusy ? 'Working…' : 'Ready to fetch the latest file.'}
                </div>
                <button
                  type="button"
                  className={styles.uploadButton}
                  onClick={handleCftcUrlFetch}
                  disabled={!cftcUrl || cftcUploadBusy}
                >
                  {cftcUploadBusy ? 'Processing…' : 'Fetch & Process'}
                </button>
              </div>
              <div className={styles.uploadDivider} />
              <div className={styles.uploadRow}>
                <div>
                  <div className={styles.settingLabel}>File</div>
                  <div className={styles.settingHint}>Accepted: .zip, .txt, or .csv</div>
                </div>
                <input
                  type="file"
                  className={styles.uploadFileInput}
                  accept=".zip,.txt,.csv"
                  onChange={handleCftcFileChange}
                />
              </div>
              <div className={styles.uploadRow}>
                <div className={styles.uploadStatus}>{cftcUploadStatus}</div>
                <button
                  type="button"
                  className={styles.uploadButton}
                  onClick={handleCftcUpload}
                  disabled={!cftcFile || cftcUploadBusy}
                >
                  {cftcUploadBusy ? 'Processing…' : 'Upload & Process'}
                </button>
              </div>
              {cftcUploadMeta && (
                <div className={styles.uploadMeta}>
                  <div>Last upload: {cftcUploadMeta.name}</div>
                  <div>{new Date(cftcUploadMeta.timestamp).toLocaleString()}</div>
                  <div>{cftcUploadMeta.summary}</div>
                </div>
              )}
            </div>
          </div>
        )}
        {activeCategory !== 'appearance' && activeCategory !== 'data' && (
          <div className={styles.settingsPanel}>
            <h3 className={styles.settingsPanelTitle}>
              {categories.find((item) => item.id === activeCategory)?.label}
            </h3>
            <p className={styles.settingsPanelDescription}>
              Settings for this category will appear here.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

function AppContent() {
  const { user, loading, signOut } = useAuth();
  const [currencyPair, setCurrencyPair] = useState('EUR/USD');
  const [seriesId, setSeriesId] = useState('');
  const [timeInterval, setTimeInterval] = useState('60min');
  const [timeZone, setTimeZone] = useState('America/New_York');
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined') {
      if (window.location.pathname.startsWith('/settings')) {
        return 'Settings';
      }
      if (window.location.pathname.startsWith('/terms')) {
        return 'Terms of Service';
      }
      if (window.location.pathname.startsWith('/privacy')) {
        return 'Privacy Policy';
      }
      if (window.location.pathname.startsWith('/cookies')) {
        return 'Cookie Policy';
      }
    }
    return 'Instruction Page';
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [availableModels2, setAvailableModels2] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedModel2, setSelectedModel2] = useState('');
  const [loadingState, setLoadingState] = useState(false);
  const [message, setMessage] = useState('');
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isUserMenuOpen, setUserMenuOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null,
  });
  const [availableDates, setAvailableDates] = useState({ start: null, end: null });
  const [currentPrice, setCurrentPrice] = useState(null);
  const [cftcSubSection, setCftcSubSection] = useState('summary');
  const [tradingStrategy, setTradingStrategy] = useState({
    platform: '',
    accountRef: '',
    executionDays: [],
    executionTime: '09:30:00.000',
    timeZone: 'ET',
    security: '',
    direction: 'BUY',
    totalContracts: 1,
    series: [{ contracts: 1, takeProfit: 10, stopLoss: 5 }]
  });

  const publicTabs = new Set([
    'Instruction Page',
    'Terms of Service',
    'Privacy Policy',
    'Cookie Policy'
  ]);

  const [pendingTab, setPendingTab] = useState(null);

  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') {
      return 'system';
    }
    try {
      return window.localStorage.getItem('appTheme') || 'system';
    } catch (err) {
      return 'system';
    }
  });
  const [fontFamily, setFontFamily] = useState(() => {
    if (typeof window === 'undefined') {
      return 'system';
    }
    try {
      return window.localStorage.getItem('ui.fontFamily') || 'system';
    } catch (err) {
      return 'system';
    }
  });
  const [fontSize, setFontSize] = useState(() => {
    if (typeof window === 'undefined') {
      return '16px';
    }
    try {
      return window.localStorage.getItem('ui.fontSize') || '16px';
    } catch (err) {
      return '16px';
    }
  });
  const [typeScalePresets, setTypeScalePresets] = useState(() => getInitialTypeScalePresets());
  const [typeScaleChoice, setTypeScaleChoice] = useState(() =>
    getInitialTypeScaleChoice(getInitialTypeScalePresets())
  );
  const typeScaleValues = React.useMemo(
    () => ({
      title: typeScalePresets.title[typeScaleChoice.title] || typeScalePresets.title.medium,
      section: typeScalePresets.section[typeScaleChoice.section] || typeScalePresets.section.medium,
      body: typeScalePresets.body[typeScaleChoice.body] || typeScalePresets.body.medium,
      label: typeScalePresets.label[typeScaleChoice.label] || typeScalePresets.label.medium,
      caption: typeScalePresets.caption[typeScaleChoice.caption] || typeScalePresets.caption.medium,
      tiny: typeScalePresets.tiny[typeScaleChoice.tiny] || typeScalePresets.tiny.medium
    }),
    [typeScalePresets, typeScaleChoice]
  );

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    const media = typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
    const applyTheme = () => {
      const resolved = theme === 'system' ? (media?.matches ? 'dark' : 'light') : theme;
      root.setAttribute('data-theme', resolved);
    };
    applyTheme();
    if (theme === 'system' && media) {
      media.addEventListener('change', applyTheme);
    }
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('appTheme', theme);
      } catch (err) {
        // Ignore storage failures.
      }
    }
    return () => {
      if (theme === 'system' && media) {
        media.removeEventListener('change', applyTheme);
      }
    };
  }, [theme]);
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    const familyMap = {
      system:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      inter:
        "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      roboto:
        "Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      ibm:
        "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
    };
    const resolvedFamily = familyMap[fontFamily] || familyMap.system;
    root.style.setProperty('--font-family', resolvedFamily);
    root.style.setProperty('--font-size-base', fontSize || '16px');
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('ui.fontFamily', fontFamily);
        window.localStorage.setItem('ui.fontSize', fontSize);
      } catch (err) {
        // Ignore storage failures.
      }
    }
  }, [fontFamily, fontSize]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    root.style.setProperty('--font-size-title', typeScaleValues.title || '1.5rem');
    root.style.setProperty('--font-size-section', typeScaleValues.section || '1.25rem');
    root.style.setProperty('--font-size-body', typeScaleValues.body || '0.9rem');
    root.style.setProperty('--font-size-label', typeScaleValues.label || '0.75rem');
    root.style.setProperty('--font-size-caption', typeScaleValues.caption || '0.75rem');
    root.style.setProperty('--font-size-tiny', typeScaleValues.tiny || '0.65rem');
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('ui.typeScaleChoice', JSON.stringify(typeScaleChoice));
        window.localStorage.setItem('ui.typeScalePresets', JSON.stringify(typeScalePresets));
        window.localStorage.setItem('ui.typeScale', JSON.stringify(typeScaleValues));
      } catch (err) {
        // Ignore storage failures.
      }
    }
  }, [typeScaleChoice, typeScalePresets, typeScaleValues]);

  const handleRelayout = (event) => {
    if (event["xaxis.range[0]"] && event["xaxis.range[1]"]) {
      setDateRange({
        start: event["xaxis.range[0]"],
        end: event["xaxis.range[1]"],
      });
    }
  };

  useEffect(() => {
    if (activeTab === 'LSTM Predict') {
      setSidebarOpen(false);
      if (availableModels.length === 0) {
        const fetchModels = async () => {
          try {
            setLoadingState(true);
            const response = await fetch('https://vidplay-api.deng-software.com/flask/models');
            if (!response.ok) {
              throw new Error('Failed to fetch models');
            }
            const data = await response.json();
            setAvailableModels(data.models || []);
          } catch (error) {
            console.error('Error fetching models:', error.message);
          } finally {
            setLoadingState(false);
          }
        };
        fetchModels();
      }
    }
  }, [activeTab, availableModels.length]);

  useEffect(() => {
    if (activeTab === 'LSTM Predict') {
      setSidebarOpen(false);
      if (availableModels2.length === 0) {
        const fetchModels = async () => {
          try {
            setLoadingState(true);
            const response = await fetch('https://vidplay-api.deng-software.com/flask/models2');
            if (!response.ok) {
              throw new Error('Failed to fetch models');
            }
            const data = await response.json();
            setAvailableModels2(data.models || []);
          } catch (error) {
            console.error('Error fetching models:', error.message);
          } finally {
            setLoadingState(false);
          }
        };
        fetchModels();
      }
    }
  }, [activeTab, availableModels2.length]);

  const handleCreateModel = async () => {
    if (!currencyPair) {
      setMessage('Please select a currency pair to create a model.');
      return;
    }

    try {
      setLoadingState(true);
      setMessage(`Creating Model 1 for ${currencyPair}`);
      const response = await fetch('https://vidplay-api.deng-software.com/flask/models/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currencyPair }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create model');
      }

      setAvailableModels((prev) => [...prev, currencyPair]);
      setMessage(`Model 1 for ${currencyPair} created successfully!`);
    } catch (error) {
      setMessage(error.message || 'Error creating model.');
    } finally {
      setLoadingState(false);
    }
  };

  const handleCreateModel2 = async () => {
    if (!currencyPair) {
      setMessage('Please select a currency pair to create a model.');
      return;
    }

    try {
      setLoadingState(true);
      setMessage(`Creating Model 2 for ${currencyPair}`);
      const response = await fetch('https://vidplay-api.deng-software.com/flask/create2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currencyPair }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create model');
      }

      setAvailableModels2((prev) => [...prev, currencyPair]);
      setMessage(`Model 2 for ${currencyPair} created successfully!`);
    } catch (error) {
      setMessage(error.message || 'Error creating model.');
    } finally {
      setLoadingState(false);
    }
  };

  const handleTabClick = (tab) => {
    if (!user && !publicTabs.has(tab)) {
      setPendingTab(tab);
      setActiveTab('Instruction Page');
      setSidebarOpen(false);
      if (typeof window !== 'undefined') {
        window.history.pushState({}, '', '/');
      }
      return;
    }
    setActiveTab(tab);
    setSidebarOpen(false);
    setUserMenuOpen(false);
    if (typeof window !== 'undefined') {
      let path = '/';
      if (tab === 'Settings') {
        path = '/settings';
      } else if (tab === 'Terms of Service') {
        path = '/terms';
      } else if (tab === 'Privacy Policy') {
        path = '/privacy';
      } else if (tab === 'Cookie Policy') {
        path = '/cookies';
      }
      if (window.location.pathname !== path) {
        window.history.pushState({}, '', path);
      }
    }
  };

  const liquidityTabs = [
    'Identify Liquidity Zone',
    'Highlight Liquidity Sweeps',
    'Visualize Order Blocks',
    'Track Fair Value Gap',
    'Track Displayment'
  ];
  const isLiquidityTab = liquidityTabs.includes(activeTab);
  const isCftcTrendView = ['trend', 'long-trend', 'short-trend', 'impact-report'].includes(cftcSubSection);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const syncTabWithLocation = () => {
      if (window.location.pathname.startsWith('/settings')) {
        setActiveTab('Settings');
        setSidebarOpen(false);
        return;
      }
      if (window.location.pathname.startsWith('/terms')) {
        setActiveTab('Terms of Service');
        setSidebarOpen(false);
        return;
      }
      if (window.location.pathname.startsWith('/privacy')) {
        setActiveTab('Privacy Policy');
        setSidebarOpen(false);
        return;
      }
      if (window.location.pathname.startsWith('/cookies')) {
        setActiveTab('Cookie Policy');
        setSidebarOpen(false);
        return;
      }
      if (activeTab === 'Settings') {
        setActiveTab('Instruction Page');
      }
    };
    syncTabWithLocation();
    window.addEventListener('popstate', syncTabWithLocation);
    return () => window.removeEventListener('popstate', syncTabWithLocation);
  }, [activeTab]);

  useEffect(() => {
    if (user && pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
      setSidebarOpen(false);
      setUserMenuOpen(false);
    }
  }, [user, pendingTab]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingSpinner />
        <p>Loading...</p>
      </div>
    );
  }

  const isPublicTab = publicTabs.has(activeTab);
  // Show login page if user is not authenticated and trying to access protected content
  if (!user && !isPublicTab) {
    return <Login />;
  }

  // Context value
  const contextValue = {
    currencyPair,
    setCurrencyPair,
    seriesId, 
    setSeriesId,
    timeInterval,
    setTimeInterval,
    timeZone,
    setTimeZone,
    activeTab,
    setActiveTab,
    availableModels,
    setAvailableModels,
    availableModels2,
    setAvailableModels2,
    selectedModel,
    setSelectedModel,
    selectedModel2,
    setSelectedModel2,
    loading: loadingState,
    setLoading: setLoadingState,
    message,
    setMessage,
    timeZones,
    g50Currencies,
    timeIntervals,
    Plot,
    LoadingSpinner,
    Dropdown,
    handleCreateModel,
    handleCreateModel2, 
    dateRange, 
    setDateRange, 
    handleRelayout, 
    availableDates, 
    setAvailableDates,
    currentPrice, 
    setCurrentPrice,
    cftcSubSection,
    setCftcSubSection,
    tradingStrategy,
    setTradingStrategy,
    theme,
    setTheme,
    fontFamily,
    setFontFamily,
    fontSize,
    setFontSize,
    typeScale: typeScaleValues,
    setTypeScale: setTypeScaleChoice,
    typeScalePresets,
    setTypeScalePresets,
    typeScaleChoice,
    user,
    signOut
  };

  const navigateLegal = (tab) => {
    handleTabClick(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AppContext.Provider value={contextValue}>
      <div className={`${styles.container} ${isSidebarOpen ? styles.containerWithSidebar : ''}`}>
        <div className={styles.stickyHeader}>
          <div className={styles.logoContainer}>
            <img
              src="/forexai2.png"
              alt="ForexAI Logo"
              className={styles.logo}
            />
          </div>

          <div className={styles.headerTitleWrap}>
            <Header title="Markets Analysis Tools (MATs)" />
          </div>

          

          <div className={styles.headerMobileControls}>
            <nav className={styles.headerNav}>
              <button
                className={`${styles.headerNavLink} ${activeTab === 'Instruction Page' ? styles.headerNavLinkActive : ''}`}
                onClick={() => handleTabClick('Instruction Page')}
              >
                Home
              </button>
            
              <div className={styles.headerNavDropdown}>
                <button
                  className={`${styles.headerNavLink} ${activeTab === 'CFTC Snapshot' ? styles.headerNavLinkActive : ''}`}
                  onClick={() => {
                    handleTabClick('CFTC Snapshot');
                    setCftcSubSection('summary');
                  }}
                >
                  CFTC Data ▾
                </button>
                <div className={styles.headerNavDropdownMenu}>
                  <button
                    className={`${styles.headerNavDropdownItem} ${cftcSubSection === 'summary' ? styles.headerNavDropdownItemActive : ''}`}
                    onClick={() => {
                      handleTabClick('CFTC Snapshot');
                      setCftcSubSection('summary');
                    }}
                  >
                    CFTC Summary
                  </button>
                  <button
                    className={`${styles.headerNavDropdownItem} ${cftcSubSection === 'trend' ? styles.headerNavDropdownItemActive : ''}`}
                    onClick={() => {
                      handleTabClick('CFTC Snapshot');
                      setCftcSubSection('trend');
                    }}
                  >
                    Futures/Options Trend
                  </button>
                  <button
                    className={`${styles.headerNavDropdownItem} ${cftcSubSection === 'impact-report' ? styles.headerNavDropdownItemActive : ''}`}
                    onClick={() => {
                      handleTabClick('CFTC Snapshot');
                      setCftcSubSection('impact-report');
                    }}
                  >
                    Positioning Impact Report
                  </button>
                </div>
              </div>
            
              <button
                className={`${styles.headerNavLink} ${activeTab === 'Trade Setups' ? styles.headerNavLinkActive : ''}`}
                onClick={() => handleTabClick('Trade Setups')}
              >
                Trade Setups
              </button>
            </nav>
            <div className={styles.headerNavMobile} aria-label="Mobile navigation">
              <button
                className={`${styles.headerNavMobileButton} ${activeTab === 'Instruction Page' ? styles.headerNavMobileButtonActive : ''}`}
                onClick={() => handleTabClick('Instruction Page')}
                aria-label="Home"
              >
                <HiOutlineHome className={styles.headerNavMobileIcon} aria-hidden="true" />
                <span className={styles.headerNavMobileLabel}>Home</span>
              </button>
              <button
                className={`${styles.headerNavMobileButton} ${activeTab === 'CFTC Snapshot' && !isCftcTrendView ? styles.headerNavMobileButtonActive : ''}`}
                onClick={() => {
                  handleTabClick('CFTC Snapshot');
                  setCftcSubSection('summary');
                }}
                aria-label="CFTC Summary"
              >
                <HiOutlinePresentationChartLine className={styles.headerNavMobileIcon} aria-hidden="true" />
                <span className={styles.headerNavMobileLabel}>CFTC</span>
              </button>
              <button
                className={`${styles.headerNavMobileButton} ${activeTab === 'CFTC Snapshot' && isCftcTrendView ? styles.headerNavMobileButtonActive : ''}`}
                onClick={() => {
                  handleTabClick('CFTC Snapshot');
                  setCftcSubSection('trend');
                }}
                aria-label="Position Trends"
              >
                <HiOutlineArrowTrendingUp className={styles.headerNavMobileIcon} aria-hidden="true" />
                <span className={styles.headerNavMobileLabel}>Trends</span>
              </button>
              <button
                className={`${styles.headerNavMobileButton} ${activeTab === 'Trade Setups' ? styles.headerNavMobileButtonActive : ''}`}
                onClick={() => handleTabClick('Trade Setups')}
                aria-label="Trade Setups"
              >
                <HiOutlineSparkles className={styles.headerNavMobileIcon} aria-hidden="true" />
                <span className={styles.headerNavMobileLabel}>Setups</span>
              </button>
              <button
                className={`${styles.headerNavMobileButton} ${activeTab === 'Settings' ? styles.headerNavMobileButtonActive : ''}`}
                onClick={() => setUserMenuOpen((prev) => !prev)}
                aria-label="Settings menu"
              >
                <HiOutlineCog6Tooth className={styles.headerNavMobileIcon} aria-hidden="true" />
                <span className={styles.headerNavMobileLabel}>Settings</span>
              </button>
            </div>

            <div className={styles.userSection}>
              <UserMenu
                user={user}
                theme={theme}
                setTheme={setTheme}
                onNavigateSettings={() => handleTabClick('Settings')}
                onSignOut={signOut}
                open={isUserMenuOpen}
                setOpen={setUserMenuOpen}
              />
            </div>
          </div>
        </div>

        <div className={`${styles.sideHeader} ${isSidebarOpen ? styles.sideHeaderOpen : ''}`}>
          <div className={styles.sideNavContent}>
            <div className={styles.tabs}>
              {Object.keys(updatedTooltipInfo).map((tab) => (
                <div key={tab}>
                  <button
                    className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ''}`}
                    onClick={() => {
                      if (tab === 'CFTC Snapshot') {
                        setCftcSubSection('summary');
                      }
                      handleTabClick(tab);
                    }}
                  >
                    {tab}
                  </button>
                  {tab === 'CFTC Snapshot' && (
                  <div className={styles.subTabs}>
                    <button
                      className={`${styles.tab} ${styles.subTab} ${
                        cftcSubSection === 'summary' ? styles.activeTab : ''
                      }`}
                      onClick={() => {
                        handleTabClick('CFTC Snapshot');
                        setCftcSubSection('summary');
                      }}
                    >
                    CFTC Summary
                    </button>
                    <button
                      className={`${styles.tab} ${styles.subTab} ${
                        cftcSubSection === 'trend' ? styles.activeTab : ''
                        }`}
                        onClick={() => {
                          handleTabClick('CFTC Snapshot');
                          setCftcSubSection('trend');
                        }}
                      >
                        Futures/Options Trend
                      </button>
                    <button
                      className={`${styles.tab} ${styles.subTab} ${
                        cftcSubSection === 'impact-report' ? styles.activeTab : ''
                        }`}
                        onClick={() => {
                          handleTabClick('CFTC Snapshot');
                          setCftcSubSection('impact-report');
                        }}
                      >
                        Positioning Impact Report
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <button
                className={`${styles.tab} ${activeTab === 'LSTM Predict' ? styles.activeTab : ''}`}
                onClick={() => handleTabClick('LSTM Predict')}
              >
                LSTM Predict
              </button>
            </div>
          </div>
        </div>

        <div className={styles.mainContent}>
          {activeTab === 'Instruction Page' && <InstructionPage />}
          {activeTab === 'LSTM Predict' && <LSTMPredictChart />}
          {activeTab === 'Identify Liquidity Zone' && <LiquidityZoneChart />}
          {activeTab === 'Highlight Liquidity Sweeps' && <LiquiditySweepChart />}
          {activeTab === 'Track Fair Value Gap' && <FVGChart />}
          {activeTab === 'Visualize Order Blocks' && <OrderBookChart />}
          {activeTab === 'Track Displayment' && <SwingHighLowChart />}
          {activeTab === 'News and Sentiment' && <NewsAndSentiment />}
          {activeTab === 'Trading Strategy' && <TradingStrategyConfig />}
          {activeTab === 'CFTC Snapshot' && <SideDrawer />}
          {activeTab === 'Trade Setups' && <TradeSetups />}
          {activeTab === 'Settings' && <SettingsPage />}
          {activeTab === 'Terms of Service' && <TermsOfService />}
          {activeTab === 'Privacy Policy' && <PrivacyPolicy />}
          {activeTab === 'Cookie Policy' && <CookiePolicy />}
        </div>

        <footer className={styles.appFooter}>
          <div className={styles.appFooterInner}>
            <div className={styles.appFooterBrand}>
              <div className={styles.appFooterTitle}>Deng Software LLC</div>
              <div className={styles.appFooterMeta}>info@deng-consulting.com</div>
            </div>
            <div className={styles.appFooterLinks}>
              <button
                className={styles.appFooterLink}
                onClick={() => navigateLegal('Terms of Service')}
              >
                Terms
              </button>
              <button
                className={styles.appFooterLink}
                onClick={() => navigateLegal('Privacy Policy')}
              >
                Privacy
              </button>
              <button
                className={styles.appFooterLink}
                onClick={() => navigateLegal('Cookie Policy')}
              >
                Cookies
              </button>
            </div>
            <div className={styles.appFooterDisclaimer}>
              Educational analytics only. Not financial advice. No trade execution.
            </div>
          </div>
        </footer>
      </div>
    </AppContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider>
        <AppContent />
    </AuthProvider>
  );
}

export default App;
